/** Per-BLOCK layout, colour and type — the block half of `commonDesignFields`.
 *
 *  A seller can space, recolour and hide a whole SECTION but cannot touch a
 *  single item inside one. Measured on the live schema: 84 sections carry ~4 own
 *  settings plus the 7 shared design fields, while ~120 block types carry a
 *  MEDIAN of 3 settings, half of them two or fewer, eleven of them none at all —
 *  and no shared design fields whatsoever. So the heading inside a section cannot
 *  be nudged, recoloured or dropped on mobile, however trivial the ask.
 *
 *  These fields close that gap the same way `commonDesignFields` did for
 *  sections, and the same way `buttonStyleFields` did for one button. Declare
 *  nothing per block: the builder appends this group to every block's inspector
 *  and the block hosts resolve it back.
 *
 *  Four shapes are load-bearing.
 *
 *  **They live OUTSIDE `def.settings`, and that is what makes them free.**
 *  `createSectionInstance`/`createBlockInstance` bake every field that declares a
 *  `default` into the stored instance (`defaultsFrom`), so putting thirteen
 *  fields on ~120 block types would write thirteen dead keys into every block a
 *  seller ever places — the same shape as the `countdown.minutes: 10` that sat
 *  unread in every stored theme. Appended by the builder instead, exactly like
 *  `commonDesignFields`, a block that nobody customises stores NOTHING and every
 *  existing theme document is byte-identical.
 *
 *  **A `theme | custom` switch rather than "blank means inherit".** A slider has
 *  no unset state: an untouched Top spacing would read 0 in the inspector while
 *  the block rendered whatever its own CSS gives it. The switch keeps the numbers
 *  honest — in `custom` the number IS what renders — and is reversible, leaving
 *  the stored values inert rather than needing them cleared one at a time.
 *
 *  **Spacing is PADDING, never margin.** Blocks carry their own margins today
 *  (`.dk-price { margin: .75rem 0 1.25rem }`, `.dk-optgroup { margin-top: 1.1rem }`),
 *  so a `margin_top` slider has no honest default: 0 would say "no space" over a
 *  block that renders 1.1rem, and emitting it on switch-to-custom would collapse
 *  the block's natural rhythm the moment a seller went in to change a colour.
 *  Padding is 0 on every block root, so 0 is true for all of them, and the two
 *  LONGHANDS are emitted rather than the shorthand so a wrapper's horizontal
 *  padding (`.dk-cartblk { padding-inline: 1.25rem }`) survives.
 *
 *  **Colours and size stay optional inside `custom`.** Shape and palette are
 *  separate asks — a seller adding breathing room to one block should not be made
 *  to pin its colours too, or the block stops following a later brand change for
 *  a reason nobody will remember. Blank = inherit.
 */
/** Settings-id prefix for every field in this group.
 *
 *  Namespaced because the ids are merged into the same `settings` bag a block's
 *  own fields write to, and a collision there is the `width` bug again: two
 *  controls, one key, two vocabularies, each silently moving the other. R16 in
 *  `@dk/theme-templates`' `validateSectionSchema` reports a block declaring a
 *  `blk_` id as an error — asserted by that package's test suite, the same way
 *  R13 is, so it fails `pnpm -r test` in CI rather than only warning. That check
 *  is only expressible because the prefix is reserved here. */
export const BLOCK_LAYOUT_PREFIX = "blk_";
/** The defaults for the fields that have one, shared by the declaration and the
 *  resolver so the inspector's number and the rendered block cannot disagree.
 *
 *  `FieldInput` renders the declared default when a key is absent, so a block
 *  switched to Custom before a slider was ever moved shows these — and the
 *  resolver falls back to the same values rather than emitting nothing, which is
 *  what keeps the control honest. Every one of them is a no-op by construction:
 *  0 padding is what a block root already has, and `""` is inherit. */
export const BLOCK_LAYOUT_DEFAULTS = {
    paddingTop: 0,
    paddingBottom: 0,
    /** 0 = full width, i.e. whatever the host column gives it. */
    maxWidth: 0,
    /** 0 = auto: the size the block's own CSS renders. */
    fontSize: 0,
};
/** Every settings id this group owns, shared by the resolvers below and by the
 *  field declarations in `block-design-fields.ts`.
 *
 *  Spelled out as literals rather than built from `BLOCK_LAYOUT_PREFIX`: these
 *  are read on the storefront's first paint, and a minifier can inline a literal
 *  into the two call sites that use it and drop this object entirely, which it
 *  cannot do for a template string it has to evaluate. The prefix is asserted
 *  against them by a test, so the two cannot drift. */
export const BLOCK_LAYOUT_ID = {
    style: "blk_style",
    paddingTop: "blk_padding_top",
    paddingBottom: "blk_padding_bottom",
    align: "blk_align",
    maxWidth: "blk_max_width",
    textColor: "blk_text_color",
    bg: "blk_bg",
    fontSize: "blk_font_size",
    fontWeight: "blk_font_weight",
    hideMobile: "blk_hide_mobile",
    hideDesktop: "blk_hide_desktop",
};
const ID = BLOCK_LAYOUT_ID;
/** A colour a theme document may carry into an inline style: hex, or a bare CSS
 *  keyword — everything the builder's colour control can produce. Anything else
 *  is dropped rather than substituted sight unseen, because a theme document is
 *  seller-authored JSON. Same rule as `button-style.ts`. */
const COLOR_RE = /^(#[0-9a-f]{3,8}|[a-z]+)$/i;
function color(v) {
    const s = typeof v === "string" ? v.trim() : "";
    return COLOR_RE.test(s) ? s : "";
}
function num(v) {
    const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
    return Number.isFinite(n) ? n : undefined;
}
const ALIGN = new Set(["left", "center", "right"]);
const WEIGHT = new Set(["400", "500", "600", "700"]);
/** Inline overrides for one block, or `{}` when it follows the theme.
 *
 *  Bound with `:style`, so these land as ELEMENT-level declarations and beat any
 *  stylesheet rule that is not `!important` — including a tenant's own
 *  `theme_css`, which is emitted after the storefront's stylesheet in every
 *  environment. That is the lesson `buttonStyleVars` records at length: a custom
 *  property alone wins the variable but loses the declaration that paints, and
 *  two shipped templates carry `.dk-btn` rules that proved it. Concrete
 *  properties here for the same reason.
 *
 *  No `--dk-blk-*` custom properties beside them: nothing reads one — no hover
 *  variant, no descendant rule — and a var nothing consumes is a dead setting
 *  waiting to be mistaken for a live one.
 *
 *  Every value is emitted only when the seller actually set it, so switching a
 *  block to Custom to change one thing leaves everything else exactly as the
 *  theme renders it. */
export function blockLayoutStyle(settings) {
    const s = settings ?? {};
    const out = {};
    if (s[ID.style] !== "custom")
        return out;
    // Longhands, never the `padding` shorthand: the cart's own block wrapper sets
    // `padding-inline`, and a shorthand here would silently drop it.
    const pt = num(s[ID.paddingTop]) ?? BLOCK_LAYOUT_DEFAULTS.paddingTop;
    if (pt > 0)
        out["padding-top"] = `${pt}px`;
    const pb = num(s[ID.paddingBottom]) ?? BLOCK_LAYOUT_DEFAULTS.paddingBottom;
    if (pb > 0)
        out["padding-bottom"] = `${pb}px`;
    const align = String(s[ID.align] ?? "");
    if (ALIGN.has(align))
        out["text-align"] = align;
    // A constrained block is centred in its column. Left-aligned would read as a
    // rendering fault far more often than as a choice, and text alignment is a
    // separate control right above it for sellers who want the other look.
    const mw = num(s[ID.maxWidth]) ?? BLOCK_LAYOUT_DEFAULTS.maxWidth;
    if (mw > 0) {
        out["max-width"] = `${mw}px`;
        out["margin-inline"] = "auto";
    }
    const fg = color(s[ID.textColor]);
    if (fg)
        out.color = fg;
    // The shorthand, deliberately: it resets `background-image`, so a seller who
    // picks a colour gets that colour rather than a theme gradient painted over it.
    const bg = color(s[ID.bg]);
    if (bg)
        out.background = bg;
    const fs = num(s[ID.fontSize]) ?? BLOCK_LAYOUT_DEFAULTS.fontSize;
    if (fs > 0)
        out["font-size"] = `${fs}px`;
    const fw = String(s[ID.fontWeight] ?? "");
    if (WEIGHT.has(fw))
        out["font-weight"] = fw;
    return out;
}
/** Responsive-visibility classes for one block, reusing the classes the section
 *  wrapper already ships (`styles.css`) so this adds no global CSS.
 *
 *  Read OUTSIDE the `theme | custom` switch: hiding a block on phones is not a
 *  restyle, and a seller who only wants that should not have to take ownership of
 *  its colours to get it. */
export function blockLayoutClass(settings) {
    const s = settings ?? {};
    const cls = [];
    if (s[ID.hideDesktop])
        cls.push("dk-hide-desktop");
    if (s[ID.hideMobile])
        cls.push("dk-hide-mobile");
    return cls.length ? cls.join(" ") : undefined;
}
//# sourceMappingURL=block-design.js.map